// Terms body extracted so it can be rendered inside both the optional
// preview dialog (footer link) and the mandatory re-acceptance gate.
export const TermsContent = () => (
  <div className="space-y-4 text-sm">
    <section>
      <h3 className="font-semibold text-base mb-2">1. Acceptance of Terms</h3>
      <p className="text-muted-foreground">
        By creating an account and using Nightspy, you agree to be bound by these Terms and Conditions. If you do not agree to these terms, you may not use our service.
      </p>
    </section>

    <section>
      <h3 className="font-semibold text-base mb-2">2. Service Description</h3>
      <p className="text-muted-foreground">
        Nightspy provides an event ticketing platform that enables organizers to create and manage events, and attendees to register for events. We act solely as a technology platform and are not responsible for the events themselves.
      </p>
    </section>

    <section>
      <h3 className="font-semibold text-base mb-2">3. Payment Processing and Service Fees</h3>
      <p className="text-muted-foreground mb-2">
        Nightspy facilitates ticket purchases through Stripe. Ticket revenue is paid directly to the event organizer's connected Stripe account. Nightspy does not hold organizer funds.
      </p>
      <p className="text-muted-foreground mb-2">
        <strong>Service Fee.</strong> Nightspy charges a platform service fee of <strong>2% + $0.50 per ticket</strong> on paid events. By default, this fee is added on top of the ticket price and paid by the attendee at checkout. Organizers may choose to absorb this fee, in which case it is deducted from the organizer's payout instead. No service fee is charged on free events.
      </p>
      <p className="text-muted-foreground mb-2">
        <strong>Taxes.</strong> Where applicable, sales tax, GST, VAT, or similar taxes are calculated at checkout and remitted by the event organizer in accordance with their tax obligations. Nightspy is not the merchant of record and does not file or remit taxes on behalf of organizers.
      </p>
      <p className="text-muted-foreground">
        <strong>Stripe processing fees</strong> are charged separately by Stripe and deducted from the organizer's payout.
      </p>
    </section>

    <section>
      <h3 className="font-semibold text-base mb-2">4. Limitation of Liability - Event Disruptions</h3>
      <p className="text-muted-foreground mb-2">
        <strong>NIGHTSPY IS NOT LIABLE FOR ANY EVENT DISRUPTIONS, CANCELLATIONS, POSTPONEMENTS, OR ISSUES.</strong> This includes but is not limited to:
      </p>
      <ul className="list-disc pl-6 text-muted-foreground space-y-1">
        <li>Event cancellations or changes by organizers</li>
        <li>Venue changes or unavailability</li>
        <li>Performance or service quality at events</li>
        <li>Safety or security issues at events</li>
        <li>Refund disputes between organizers and attendees</li>
        <li>Weather-related disruptions or acts of God</li>
        <li>Technical issues preventing event check-in</li>
      </ul>
      <p className="text-muted-foreground mt-2">
        Event organizers are solely responsible for the execution, quality, and any issues related to their events. Any disputes must be resolved directly with the event organizer.
      </p>
    </section>

    <section>
      <h3 className="font-semibold text-base mb-2">4. User Responsibilities</h3>
      <p className="text-muted-foreground mb-2">As a user, you agree to:</p>
      <ul className="list-disc pl-6 text-muted-foreground space-y-1">
        <li>Provide accurate and truthful information</li>
        <li>Maintain the confidentiality of your account credentials</li>
        <li>Not use the platform for illegal or fraudulent purposes</li>
        <li>Not attempt to disrupt or compromise the platform's security</li>
        <li>Comply with all applicable laws and regulations</li>
      </ul>
    </section>

    <section>
      <h3 className="font-semibold text-base mb-2">5. Organizer Responsibilities</h3>
      <p className="text-muted-foreground mb-2">Event organizers agree to:</p>
      <ul className="list-disc pl-6 text-muted-foreground space-y-1">
        <li>Provide accurate event information</li>
        <li>Honor all tickets sold through the platform</li>
        <li>Handle all refund requests fairly and in accordance with stated policies</li>
        <li>Comply with all applicable laws, permits, and regulations for their events</li>
        <li>Maintain adequate insurance for their events</li>
        <li>Be solely responsible for the safety and execution of their events</li>
      </ul>
    </section>

    <section>
      <h3 className="font-semibold text-base mb-2">6. Refunds and Disputes</h3>
      <p className="text-muted-foreground mb-2">
        Refund policies for ticket purchases are set and administered by individual event organizers. All refund requests, chargebacks, and payment disputes related to the ticket price must be resolved directly with the organizer of the event.
      </p>
      <p className="text-muted-foreground mb-2">
        <strong>Service fees are non-refundable.</strong> The Nightspy platform service fee (2% + $0.50 per ticket) is retained by Nightspy regardless of whether the ticket is refunded, the event is cancelled, postponed, or rescheduled, except where required by law.
      </p>
      <p className="text-muted-foreground">
        Nightspy is not responsible for issuing refunds for ticket prices or for resolving disputes between organizers and attendees.
      </p>
    </section>

    <section>
      <h3 className="font-semibold text-base mb-2">7. Platform Availability</h3>
      <p className="text-muted-foreground">
        While we strive to maintain consistent service availability, Nightspy is provided "as is" without warranties of any kind. We do not guarantee uninterrupted access and are not liable for any downtime, technical issues, or data loss.
      </p>
    </section>

    <section>
      <h3 className="font-semibold text-base mb-2">8. Privacy and Data</h3>
      <p className="text-muted-foreground">
        Your use of Nightspy is subject to our Privacy Policy. By using our service, you consent to our collection and use of your data as described. We take reasonable measures to protect your information but cannot guarantee absolute security.
      </p>
    </section>

    <section>
      <h3 className="font-semibold text-base mb-2">9. Intellectual Property</h3>
      <p className="text-muted-foreground">
        All content, features, and functionality of Nightspy are owned by us and protected by intellectual property laws. You may not copy, modify, or distribute any part of our platform without permission.
      </p>
    </section>

    <section>
      <h3 className="font-semibold text-base mb-2">10. Indemnification</h3>
      <p className="text-muted-foreground">
        You agree to indemnify and hold harmless Nightspy, its officers, directors, employees, and agents from any claims, damages, losses, or expenses arising from your use of the platform, your violation of these terms, or your violation of any rights of others.
      </p>
    </section>

    <section>
      <h3 className="font-semibold text-base mb-2">11. Dispute Resolution</h3>
      <p className="text-muted-foreground">
        Any disputes arising from these terms will be resolved through binding arbitration in accordance with Canadian law. You waive your right to participate in class action lawsuits.
      </p>
    </section>

    <section>
      <h3 className="font-semibold text-base mb-2">12. Termination and Suspension</h3>
      <p className="text-muted-foreground mb-2">
        We reserve the right to suspend or terminate your account at any time for violation of these terms or any reason we deem appropriate. You may also terminate your account at any time.
      </p>
      <p className="text-muted-foreground">
        <strong>Nightspy may suspend or remove events or organizers suspected of fraud or policy violation.</strong> This includes but is not limited to fraudulent events, scams, misleading event information, or any activity that violates these terms or applicable laws. We reserve the right to take immediate action without prior notice to protect our users and platform integrity.
      </p>
    </section>

    <section>
      <h3 className="font-semibold text-base mb-2">13. Beta Status</h3>
      <p className="text-muted-foreground">
        Nightspy is currently in beta. Features may change, and the service may experience interruptions or modifications without notice. We appreciate your understanding as we continue to improve the platform.
      </p>
    </section>

    <section>
      <h3 className="font-semibold text-base mb-2">14. Changes to Terms</h3>
      <p className="text-muted-foreground">
        We reserve the right to modify these terms at any time. Continued use of the platform after changes constitutes acceptance of the modified terms.
      </p>
    </section>

    <section>
      <h3 className="font-semibold text-base mb-2">15. Contact Information</h3>
      <p className="text-muted-foreground">
        For questions about these terms, please contact us through the platform or via email at support@nightspy.app.
      </p>
    </section>

    <section className="pt-4 border-t">
      <p className="text-muted-foreground text-xs italic">
        Last Updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
      </p>
    </section>
  </div>
);
